import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { GO_SERVICE_QUERY_KEYS } from "../api/queryKeys";
import { ServiceContract } from "../types/service.type";
import { useStripePayModal } from "../components/StripePayModal";
import { hasPaidMilestone } from "../utils/contractBusinessLogic";
import { ContractPaymentData } from "../api/types/contract.types";
import { useCreateContractPayment } from "../api/mutations/useCreateContractPayment";

interface UseContractPaymentParams {
  contractDetails: ServiceContract | undefined;
  contractId: number | null | undefined;
  serviceRequestId: number;
  isProvider: boolean;
}

/**
 * Hook that manages contract payment creation, state, and modal handling.
 * Handles automatic payment creation when needed and provides payment handlers.
 */
export const useContractPayment = ({
  contractDetails,
  contractId,
  serviceRequestId,
  isProvider,
}: UseContractPaymentParams) => {
  const queryClient = useQueryClient();
  const [paymentData, setPaymentData] = useState<ContractPaymentData | null>(null);

  const { openStripePayModal, handleOpenStripePayModal, handleCloseStripePayModal, clientSecret, publishableKey } =
    useStripePayModal();

  const { mutate: createContractPayment, isPending: isCreatingPayment } = useCreateContractPayment({
    onSuccess: (data) => {
      setPaymentData(data);
      // Invalidate contract details query to refetch updated payment data
      if (contractId) {
        queryClient.invalidateQueries({
          queryKey: GO_SERVICE_QUERY_KEYS.contractDetails(contractId),
        });
      }
    },
  });

  // Sync payment data when contract details refetch
  useEffect(() => {
    if (!contractDetails || isProvider || !contractId || isCreatingPayment) return;

    // Check if payment is already completed by looking at milestones
    const milestonePaid = hasPaidMilestone(contractDetails.milestones);

    // Only create payment if:
    // 1. We don't have payment data yet
    // 2. No milestone has been paid yet (payment not completed)
    // 3. Contract is in a state that requires payment
    const needsPayment = !paymentData && !milestonePaid;

    if (needsPayment) {
      createContractPayment(contractId);
    }
  }, [
    contractDetails?.id,
    contractDetails?.milestones,
    isProvider,
    contractId,
    paymentData,
    isCreatingPayment,
    createContractPayment,
  ]);

  const handleOpenPaymentModal = () => {
    if (paymentData?.clientSecret && paymentData?.publishableKey) {
      handleOpenStripePayModal(paymentData.clientSecret, paymentData.publishableKey);
    }
  };

  const handlePaymentSuccess = async () => {
    // Invalidate and refetch contract details to get updated payment status and milestones
    if (contractId) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: GO_SERVICE_QUERY_KEYS.contractDetails(contractId),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: GO_SERVICE_QUERY_KEYS.serviceRequest(serviceRequestId),
          refetchType: "active",
        }),
      ]);
    }
    // Reset payment data after queries have refetched
    // This prevents creating a new payment if the refetch shows payment is complete
    setPaymentData(null);
  };

  return {
    paymentData,
    isCreatingPayment,
    handleOpenPaymentModal,
    handlePaymentSuccess,
    stripeModalProps: {
      open: openStripePayModal,
      closeModal: handleCloseStripePayModal,
      clientSecret,
      publishableKey,
    },
  };
};
